import React, { useState, useRef, useEffect } from 'react';
import './BlockReportMenu.css';

export type ReportReason =
  | 'inappropriate_content'
  | 'fake_profile'
  | 'harassment'
  | 'spam'
  | 'underage'
  | 'other';

export const REPORT_REASONS: { value: ReportReason; label: string }[] = [
  { value: 'inappropriate_content', label: 'Inappropriate content' },
  { value: 'fake_profile', label: 'Fake profile' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'spam', label: 'Spam or scam' },
  { value: 'underage', label: 'Appears underage' },
  { value: 'other', label: 'Other' },
];

interface BlockReportMenuProps {
  profileId: string;
  profileName: string;
  onBlock: (profileId: string) => Promise<void>;
  onReport: (profileId: string, reason: ReportReason, details?: string) => Promise<void>;
  onUnmatch?: (profileId: string) => Promise<void>;
  showUnmatch?: boolean;
  variant?: 'icon' | 'text';
  className?: string;
}

export const BlockReportMenu: React.FC<BlockReportMenuProps> = ({
  profileId,
  profileName,
  onBlock,
  onReport,
  onUnmatch,
  showUnmatch = false,
  variant = 'icon',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showConfirmBlock, setShowConfirmBlock] = useState(false);
  const [showConfirmUnmatch, setShowConfirmUnmatch] = useState(false);
  const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);
  const [reportDetails, setReportDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBlock = async () => {
    setIsSubmitting(true);
    try {
      await onBlock(profileId);
      setSuccess('User blocked successfully');
      setShowConfirmBlock(false);
      setIsOpen(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to block user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnmatch = async () => {
    if (!onUnmatch) return;
    setIsSubmitting(true);
    try {
      await onUnmatch(profileId);
      setSuccess('Unmatched successfully');
      setShowConfirmUnmatch(false);
      setIsOpen(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to unmatch:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReport = async () => {
    if (!selectedReason) return;
    setIsSubmitting(true);
    try {
      await onReport(profileId, selectedReason, reportDetails || undefined);
      setSuccess('Report submitted. Thank you for keeping our community safe.');
      setShowReportModal(false);
      setSelectedReason(null);
      setReportDetails('');
      setIsOpen(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to submit report:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeAllModals = () => {
    setShowReportModal(false);
    setShowConfirmBlock(false);
    setShowConfirmUnmatch(false);
    setSelectedReason(null);
    setReportDetails('');
  };

  return (
    <>
      {/* `brm-root` is the scoping marker every rule in BlockReportMenu.css hangs
          off (theming-laws.md law 8). This component renders a fragment with
          several roots, so each one carries it. */}
      <div className={`brm-root block-report-menu ${className}`} ref={menuRef}>
        <button
          className={`menu-trigger ${variant}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="More options"
        >
          {variant === 'icon' ? (
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
          ) : (
            <span>More</span>
          )}
        </button>

        {isOpen && (
          <div className="menu-dropdown">
            <button
              className="menu-item report"
              onClick={() => {
                setShowReportModal(true);
                setIsOpen(false);
              }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z" />
              </svg>
              <span>Report {profileName}</span>
            </button>

            <button
              className="menu-item block"
              onClick={() => {
                setShowConfirmBlock(true);
                setIsOpen(false);
              }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z" />
              </svg>
              <span>Block {profileName}</span>
            </button>

            {showUnmatch && onUnmatch && (
              <button
                className="menu-item unmatch"
                onClick={() => {
                  setShowConfirmUnmatch(true);
                  setIsOpen(false);
                }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3zm-4.4 15.55l-.1.1-.1-.1C7.14 14.24 4 11.39 4 8.5 4 6.5 5.5 5 7.5 5c1.54 0 3.04.99 3.57 2.36h1.87C13.46 5.99 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5 0 2.89-3.14 5.74-7.9 10.05z" />
                </svg>
                <span>Unmatch</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <div className="brm-root modal-overlay" onClick={closeAllModals}>
          <div className="report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Report {profileName}</h3>
              <button className="close-btn" onClick={closeAllModals}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <p className="report-description">
                Help us understand what's happening. Your report is anonymous and helps keep our community safe.
              </p>

              <div className="report-reasons">
                {REPORT_REASONS.map((reason) => (
                  <label key={reason.value} className="reason-option">
                    <input
                      type="radio"
                      name="reportReason"
                      value={reason.value}
                      checked={selectedReason === reason.value}
                      onChange={() => setSelectedReason(reason.value)}
                    />
                    <span className="reason-label">{reason.label}</span>
                  </label>
                ))}
              </div>

              {selectedReason && (
                <div className="report-details">
                  <label>Additional details (optional)</label>
                  <textarea
                    value={reportDetails}
                    onChange={(e) => setReportDetails(e.target.value)}
                    placeholder="Provide any additional context..."
                    rows={3}
                  />
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={closeAllModals}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleReport}
                disabled={!selectedReason || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block Confirmation Modal */}
      {showConfirmBlock && (
        <div className="brm-root modal-overlay" onClick={closeAllModals}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Block {profileName}?</h3>
            </div>
            <div className="modal-body">
              <p>
                {profileName} won't be able to see your profile or message you.
                They won't be notified that you blocked them.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={closeAllModals}>
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={handleBlock}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Blocking...' : 'Block'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unmatch Confirmation Modal */}
      {showConfirmUnmatch && (
        <div className="brm-root modal-overlay" onClick={closeAllModals}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Unmatch with {profileName}?</h3>
            </div>
            <div className="modal-body">
              <p>
                This will remove {profileName} from your matches and delete your conversation.
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={closeAllModals}>
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={handleUnmatch}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Unmatching...' : 'Unmatch'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {success && (
        <div className="brm-root success-toast">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
          </svg>
          <span>{success}</span>
        </div>
      )}
    </>
  );
};

export default BlockReportMenu;
